import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
// import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'; // COMMENTED OUT - not used in map-only view
import {
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  MapPin,
  Users,
  Clock,
  Navigation,
  Download,
  Send,
  RefreshCw
} from 'lucide-react';
import TechnicianCard from '../components/TechnicianCard';
import RouteDetailsPanel from '../components/RouteDetailsPanel';
// import { TechnicianRouteCard } from '../components/TechnicianRouteCard'; // COMMENTED OUT - not used in map-only view
import { useRouteConfirmation } from '../hooks/useRouteConfirmation';
import useSelectedRecalls from '@/hooks/useSelectedRecalls';
import useTechnicianAssignments from '@/hooks/useTechnicianAssignments';
import { useDealerManagementSimple } from '@/hooks/useDealerManagementSimple';
import { printRouteSheet, printAllRoutes } from '../utils/printRoutes';
import { useToast } from '@/hooks/use-toast';

interface DealerLocation {
  id: string;
  name: string;
  address: string;
  city: string;
  region: string;
  postal_code: string;
  phone: string;
  latitude: number;
  longitude: number;
  recall_count: number;
  skill_level: number;
}

interface Technician {
  id: string;
  full_name: string;
  skill_level: number;
  phone_number: string;
  email: string;
  home_base?: {
    latitude: number;
    longitude: number;
  };
}

interface RouteStop {
  id: string;
  dealer: DealerLocation;
  arrival_time: string;
  departure_time: string;
  service_duration: number;
  stop_order: number;
}

interface TechnicianRoute {
  technician_id: string;
  technician: Technician;
  stops: RouteStop[];
  total_distance: number;
  total_duration: number;
  route_color: string;
}

const RouteConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { toast } = useToast();
  const { selectedRecalls } = useSelectedRecalls();
  const { assignments, activeTechnicians } = useTechnicianAssignments();

  // Get userId from session, or use a default for development
  const userId = session?.user?.id || 'dev-user-' + Date.now();
  const { baseLocation } = useDealerManagementSimple(userId);
  const { routes, loading, confirmRoutes, isConfirming } = useRouteConfirmation();

  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);

  // Build routes from current assignments
  const buildRoutesFromAssignments = (): TechnicianRoute[] => {
    if (!assignments.length || !activeTechnicians.length) return [];

    const routeColors = [
      '#3b82f6',
      '#ef4444',
      '#10b981',
      '#f59e0b',
      '#8b5cf6',
      '#06b6d4',
      '#f97316',
      '#84cc16'
    ];

    // Group assignments by technician
    const assignmentsByTechnician = assignments.reduce(
      (acc: Record<string, any[]>, assignment: any) => {
        if (!acc[assignment.technician_id]) {
          acc[assignment.technician_id] = [];
        }
        acc[assignment.technician_id]!.push(assignment);
        return acc;
      },
      {} as Record<string, any[]>
    );

    // Build routes
    const builtRoutes = Object.entries(assignmentsByTechnician)
      .map(([technicianId, technicianAssignments], index) => {
        const technician = activeTechnicians.find((t: any) => t.id === technicianId);
        if (!technician) return null;

        // Use base location coordinates if technician doesn't have home_base
        const homeBase =
          technician.home_base ||
          (baseLocation?.coordinates
            ? {
                latitude: baseLocation.coordinates.lat,
                longitude: baseLocation.coordinates.lng
              }
            : null);

        if (!homeBase) return null;

        // Get dealer locations from selected recalls
        const dealerLocations = technicianAssignments
          .map((assignment: any) => {
            const recall = selectedRecalls.find(
              (r: any) =>
                r.vin === assignment.vin && r.campaign_number === assignment.campaign_number
            );

            if (!recall) return null;

            return {
              id: `${recall.seller_name || recall.dealer_name}-${recall.dealer_city}`,
              name: recall.seller_name || recall.dealer_name || 'Unknown Dealer',
              address: recall.dealer_street || recall.seller_address || '',
              city: recall.dealer_city || recall.seller_city || '',
              region: recall.dealer_region || recall.seller_region || '',
              postal_code: recall.dealer_postal_code || recall.seller_postal_code || '',
              phone: recall.dealer_phone || recall.seller_phone || '',
              latitude: recall.dealer_latitude || 0,
              longitude: recall.dealer_longitude || 0,
              recall_count: 1,
              skill_level: recall.skill_level || 2 // Use recall skill level or default
            };
          })
          .filter(
            (location): location is DealerLocation =>
              location !== null && location.latitude !== 0 && location.longitude !== 0
          );

        // Create route stops with recall data
        const stops: RouteStop[] = dealerLocations.map((dealer, stopIndex) => {
          // Find all assignments for this dealer
          const dealerAssignments = technicianAssignments.filter((assignment: any) => {
            const recall = selectedRecalls.find(
              (r: any) =>
                r.vin === assignment.vin && r.campaign_number === assignment.campaign_number
            );
            return recall && (recall.seller_name || recall.dealer_name) === dealer.name;
          });

          // Get recall data for this dealer
          const recalls = dealerAssignments
            .map((assignment: any) => {
              const recall = selectedRecalls.find(
                (r: any) =>
                  r.vin === assignment.vin && r.campaign_number === assignment.campaign_number
              );
              return recall;
            })
            .filter(Boolean);

          return {
            id: `${technicianId}-${dealer.id}`,
            dealer,
            arrival_time: new Date(Date.now() + stopIndex * 60 * 60 * 1000).toISOString(),
            departure_time: new Date(
              Date.now() + stopIndex * 60 * 60 * 1000 + 60 * 60 * 1000
            ).toISOString(),
            service_duration: 60,
            stop_order: stopIndex + 1,
            recalls // Now includes the recall data
          };
        });

        // Calculate total distance and duration (simplified)
        const totalDistance = stops.length * 15; // Assume 15 miles between stops
        const totalDuration = stops.length * 60; // 60 minutes per stop

        return {
          technician_id: technicianId,
          technician: {
            ...technician,
            home_base: homeBase
          },
          stops,
          total_distance: totalDistance,
          total_duration: totalDuration,
          route_color: routeColors[index % routeColors.length]
        };
      })
      .filter((route): route is TechnicianRoute => route !== null);

    return builtRoutes;
  };

  const currentRoutes = routes.length > 0 ? routes : buildRoutesFromAssignments();

  // Set first technician as selected by default
  useEffect(() => {
    if (currentRoutes.length > 0 && !selectedRouteId && currentRoutes[0]) {
      setSelectedRouteId(currentRoutes[0].technician_id);
    }
  }, [currentRoutes, selectedRouteId]);

  const handleRouteSelect = (route: TechnicianRoute) => {
    setSelectedRouteId(route.technician_id);
  };

  const handleConfirmRoutes = async () => {
    try {
      await confirmRoutes(currentRoutes);
      navigate('/routing/manual');
    } catch (error) {}
  };

  const handleBackToEdit = () => {
    navigate('/routing/manual');
  };

  const handlePrintAllRoutes = async () => {
    if (currentRoutes.length === 0) {
      toast({
        title: 'No Routes',
        description: 'No routes available to print',
        variant: 'default'
      });
      return;
    }
    await printAllRoutes(currentRoutes);
  };

  const handlePrintRoute = async (route: TechnicianRoute) => {
    await printRouteSheet(route);
  };

  const handleSendToTechnicians = () => {
    // TODO: Implement send functionality
    toast({
      title: 'Coming Soon',
      description: 'Send to technicians functionality will be implemented soon',
      variant: 'default'
    });
  };

  const totalRecalls = currentRoutes.reduce((sum, route) => sum + route.stops.length, 0);
  const totalDistance = currentRoutes.reduce((sum, route) => sum + route.total_distance, 0);
  const totalDuration = currentRoutes.reduce((sum, route) => sum + route.total_duration, 0);

  if (loading) {
    return (
      <div className='flex justify-center items-center min-h-[60vh]'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary' />
      </div>
    );
  }

  if (currentRoutes.length === 0) {
    return (
      <div className='p-4'>
        <Card className='p-8 text-center'>
          <AlertTriangle className='h-12 w-12 text-amber-500 mx-auto mb-4' />
          <h2 className='text-xl font-semibold mb-2'>No Routes to Confirm</h2>
          <p className='text-muted-foreground mb-4'>
            You need to assign technicians to recalls before confirming routes.
          </p>
          <Button onClick={handleBackToEdit}>
            <ArrowLeft className='w-4 h-4 mr-2' />
            Back to Technician Assignment
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className='w-full h-full flex flex-col'>
      {/* Header */}
      <div className='mb-4 flex-shrink-0 px-4'>
        <div className='flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4'>
          <div>
            <h1 className='text-2xl lg:text-4xl font-bold'>Route Confirmation</h1>
            <p className='text-muted-foreground'>
              Review and confirm technician routes before sending
            </p>
          </div>

          <div className='flex flex-col sm:flex-row gap-2'>
            <Button variant='outline' onClick={handleBackToEdit}>
              <ArrowLeft className='w-4 h-4 mr-2' />
              Back to Edit
            </Button>
            <Button variant='outline' onClick={handlePrintAllRoutes}>
              <Download className='w-4 h-4 mr-2' />
              Print All Routes
            </Button>
            <Button variant='outline' onClick={handleSendToTechnicians}>
              <Send className='w-4 h-4 mr-2' />
              Send to Technicians
            </Button>
            <Button
              onClick={handleConfirmRoutes}
              disabled={isConfirming}
              className='bg-green-600 hover:bg-green-700'
            >
              {isConfirming ? (
                <>
                  <RefreshCw className='w-4 h-4 mr-2 animate-spin' />
                  Confirming...
                </>
              ) : (
                <>
                  <CheckCircle className='w-4 h-4 mr-2' />
                  Confirm Routes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <Card className='mb-4 flex-shrink-0 mx-4'>
        <CardContent className='p-4'>
          <div className='flex flex-wrap gap-4'>
            <div className='flex items-center gap-2'>
              <Users className='h-4 w-4 text-blue-600' />
              <span className='text-sm font-medium'>{currentRoutes.length} Technicians</span>
            </div>
            <div className='flex items-center gap-2'>
              <MapPin className='h-4 w-4 text-green-600' />
              <span className='text-sm font-medium'>{totalRecalls} Total Recalls</span>
            </div>
            <div className='flex items-center gap-2'>
              <Navigation className='h-4 w-4 text-purple-600' />
              <span className='text-sm font-medium'>{totalDistance.toFixed(1)} miles</span>
            </div>
            <div className='flex items-center gap-2'>
              <Clock className='h-4 w-4 text-orange-600' />
              <span className='text-sm font-medium'>{Math.round(totalDuration / 60)} hours</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content - 2 Column Layout */}
      <div className='flex-grow flex gap-4 min-h-0 px-4'>
        {/* Left Column - Technician Cards */}
        <div className='w-80 flex-shrink-0'>
          <div className='sticky top-4'>
            <div className='flex items-center gap-2 mb-4'>
              <Users className='w-5 h-5 text-blue-600' />
              <h3 className='text-lg font-semibold'>Technicians</h3>
              <Badge variant='outline' className='ml-2'>
                {currentRoutes.length} assigned
              </Badge>
            </div>

            {currentRoutes.length > 0 ? (
              <div className='space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto'>
                {currentRoutes.map(route => (
                  <TechnicianCard
                    key={route.technician_id}
                    route={route}
                    isSelected={selectedRouteId === route.technician_id}
                    onSelect={handleRouteSelect}
                    onPrintRoute={handlePrintRoute}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className='p-6 text-center'>
                  <Users className='w-12 h-12 text-gray-400 mx-auto mb-4' />
                  <h4 className='text-lg font-medium text-gray-900 mb-2'>
                    No Assigned Technicians
                  </h4>
                  <p className='text-gray-600'>
                    Go back to assign technicians to recalls before confirming routes.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Right Column - Route Details and Map */}
        <div className='flex-1 min-w-0'>
          <RouteDetailsPanel
            selectedRoute={
              currentRoutes.find(route => route.technician_id === selectedRouteId) || null
            }
            baseLocation={
              baseLocation
                ? {
                    lat: baseLocation.coordinates?.lat || 0,
                    lng: baseLocation.coordinates?.lng || 0,
                    dealerName: baseLocation.dealerName,
                    address: `${baseLocation.address}, ${baseLocation.city}, ${baseLocation.state}`
                  }
                : null
            }
          />
        </div>
      </div>
    </div>
  );
};

export default RouteConfirmationPage;
